import React from 'react';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';
import { FaCalendarAlt, FaMapMarkerAlt, FaTrophy, FaCheckCircle, FaInfoCircle, FaAward, FaUsers, FaChartLine, FaLightbulb, FaRocket, FaHandshake, FaArrowLeft } from 'react-icons/fa';
import EventGallery from '../components/EventGallery';

function Festival() {
  const navigate = useNavigate();
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-amber-50 to-orange-50 dark:from-gray-900 dark:via-slate-900 dark:to-gray-900 transition-colors duration-300 py-20">
      {/* SEO Meta Tags */}
      <Helmet>
        <title>جشنواره امید | آرشیو جشنواره‌های نوآفرین‌شو</title>
        <meta name="description" content="جشنواره امید - آرشیو رویدادهای مرکز رشد دانشگاه شمال" />
        <meta name="keywords" content="جشنواره امید، آرشیو، هسته فناور، دانشگاه شمال" />
      </Helmet>
      
      <div className="container mx-auto px-4">
        {/* Back Button */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.5 }}
          className="mb-8"
        >
          <button
            onClick={() => navigate('/festivals')}
            className="inline-flex items-center gap-2 px-4 py-2 bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm text-gray-700 dark:text-gray-300 rounded-xl hover:bg-white dark:hover:bg-gray-800 transition-all shadow-md hover:shadow-lg"
          >
            <FaArrowLeft className="text-lg" />
            بازگشت به لیست جشنواره‌ها
          </button>
        </motion.div>

        {/* Festival Header - Enhanced */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12 md:mb-16"
        >
          {/* Status Badge with Icon */}
          <motion.div
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="inline-block mb-6"
          >
            <span className="inline-flex items-center gap-3 bg-gradient-to-r from-gray-600 to-gray-700 text-white px-8 py-4 rounded-2xl font-bold text-lg shadow-2xl border-2 border-gray-500">
              <FaTrophy className="text-3xl" />
              جشنواره پایان یافته
            </span>
          </motion.div>
          
          {/* Main Title with Trophy Animation */}
          <motion.h1 
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="text-5xl sm:text-6xl md:text-7xl lg:text-8xl font-bold mb-8 bg-gradient-to-r from-amber-600 via-orange-500 to-red-500 dark:from-amber-500 dark:via-orange-400 dark:to-red-400 bg-clip-text text-transparent"
          >
            جشنواره امید
          </motion.h1>
          
          {/* Subtitle */}
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="text-xl md:text-2xl font-bold text-gray-700 dark:text-gray-300 mb-8"
          >
            جشنواره نوآوری و فناوری
          </motion.p>
          
          {/* Info Banner */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.5 }}
            className="max-w-4xl mx-auto mb-8"
          >
            <div className="bg-gradient-to-r from-amber-100 via-yellow-100 to-orange-100 dark:from-amber-900/30 dark:via-yellow-900/30 dark:to-orange-900/30 rounded-2xl p-6 border-2 border-amber-400 dark:border-amber-600 shadow-xl">
              <p className="text-lg md:text-xl text-gray-800 dark:text-gray-200 font-semibold flex items-center justify-center gap-3 flex-wrap">
                <FaInfoCircle className="text-amber-600 dark:text-amber-400 text-2xl flex-shrink-0" />
                <span className="text-center">
                  این جشنواره با موفقیت به پایان رسید. برای مشاهده جشنواره‌های فعال، به 
                  <button 
                    onClick={() => navigate('/')}
                    className="text-primary-600 dark:text-primary-400 hover:underline mx-2 font-bold"
                  >
                    صفحه اصلی
                  </button>
                  مراجعه کنید.
                </span>
              </p>
            </div>
          </motion.div>
          
          {/* Description */}
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.6 }}
            className="text-base md:text-lg text-gray-600 dark:text-gray-400 max-w-4xl mx-auto leading-relaxed"
          >
            این جشنواره فرصتی استثنایی برای تبدیل ایده‌های نوآورانه به هسته‌های فناور فراهم کرد. 
            با مشارکت بیش از ۲۵۰ نفر و ثبت بیش از ۲۴۸ طرح، این رویداد یکی از موفق‌ترین جشنواره‌های 
            نوآوری منطقه بود.
          </motion.p>
        </motion.div>

        {/* Festival Stats - Highlighted */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.7 }}
          className="max-w-5xl mx-auto mb-12 md:mb-16"
        >
          <div className="bg-gradient-to-br from-white via-amber-50 to-orange-50 dark:from-gray-800 dark:via-gray-700 dark:to-gray-800 rounded-3xl p-8 md:p-10 shadow-2xl border-2 border-amber-300 dark:border-amber-700">
            <h3 className="text-2xl md:text-3xl font-bold text-center mb-8 text-gray-800 dark:text-gray-200 flex items-center justify-center gap-3">
              <FaChartLine className="text-amber-600 dark:text-amber-400" />
              دستاوردهای جشنواره امید
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <motion.div
                whileHover={{ scale: 1.05, y: -5 }}
                className="bg-gradient-to-br from-primary-600 to-secondary-600 dark:from-primary-700 dark:to-secondary-700 rounded-2xl p-6 text-center text-white shadow-xl"
              >
                <FaUsers className="text-5xl mx-auto mb-4 opacity-90" />
                <div className="text-5xl font-extrabold mb-2">248+</div>
                <div className="text-lg font-semibold opacity-90">طرح ثبت شده</div>
                <p className="text-sm opacity-75 mt-2">ایده‌های نوآورانه در حوزه‌های مختلف</p>
              </motion.div>
              
              <motion.div
                whileHover={{ scale: 1.05, y: -5 }}
                className="bg-gradient-to-br from-amber-600 to-orange-600 dark:from-amber-700 dark:to-orange-700 rounded-2xl p-6 text-center text-white shadow-xl"
              >
                <FaAward className="text-5xl mx-auto mb-4 opacity-90" />
                <div className="text-5xl font-extrabold mb-2">200+</div>
                <div className="text-lg font-semibold opacity-90">هسته فناور</div>
                <p className="text-sm opacity-75 mt-2">تیم‌های موفق ثبت شده</p>
              </motion.div>
              
              <motion.div
                whileHover={{ scale: 1.05, y: -5 }}
                className="bg-gradient-to-br from-purple-600 to-pink-600 dark:from-purple-700 dark:to-pink-700 rounded-2xl p-6 text-center text-white shadow-xl"
              >
                <FaTrophy className="text-5xl mx-auto mb-4 opacity-90" />
                <div className="text-5xl font-extrabold mb-2">250+</div>
                <div className="text-lg font-semibold opacity-90">مشارکت‌کننده</div>
                <p className="text-sm opacity-75 mt-2">در رویدادهای جشنواره</p>
              </motion.div>
            </div>
          </div>
        </motion.div>

        {/* Festival Poster */}
        <div className="max-w-4xl mx-auto mb-12 md:mb-16">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.8 }}
            className="relative group"
          >
            <div className="absolute inset-0 bg-gradient-to-r from-amber-500 to-orange-500 opacity-20 blur-2xl rounded-3xl"></div>
            <div className="relative bg-white dark:bg-gray-800 rounded-3xl overflow-hidden shadow-2xl border-2 border-amber-300 dark:border-amber-600">
              <img 
                src="https://customer-assets.emergentagent.com/job_omid-festival/artifacts/stb9it9r_IMG-20251109-WA0005.jpg"
                alt="پوستر جشنواره امید"
                className="w-full h-auto object-contain max-h-[500px] md:max-h-[600px] lg:max-h-[700px] mx-auto"
              />
            </div>
          </motion.div>
        </div>

        {/* About Festival */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.9 }}
          className="max-w-5xl mx-auto mb-12 md:mb-16"
        >
          <div className="bg-gradient-to-br from-white via-blue-50 to-purple-50 dark:from-gray-800 dark:via-gray-700 dark:to-gray-800 rounded-3xl p-8 md:p-10 shadow-2xl border-2 border-blue-200 dark:border-blue-700">
            <h3 className="text-2xl md:text-3xl font-bold text-center mb-8 text-gray-800 dark:text-gray-200 flex items-center justify-center gap-3">
              <FaLightbulb className="text-amber-500" />
              درباره جشنواره امید
            </h3>
            
            <div className="grid md:grid-cols-2 gap-6 mb-8">
              <div className="bg-white/60 dark:bg-gray-800/60 rounded-xl p-6">
                <div className="flex items-start gap-4 mb-4">
                  <div className="bg-gradient-to-br from-primary-500 to-secondary-500 rounded-xl p-3">
                    <FaRocket className="text-2xl text-white" />
                  </div>
                  <div>
                    <h4 className="text-lg font-bold text-gray-800 dark:text-gray-200 mb-2">شتاب‌دهی ایده‌ها</h4>
                    <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
                      تبدیل ایده‌های خام به طرح‌های اجرایی با مشاوره تخصصی
                    </p>
                  </div>
                </div>
              </div>
              
              <div className="bg-white/60 dark:bg-gray-800/60 rounded-xl p-6">
                <div className="flex items-start gap-4 mb-4">
                  <div className="bg-gradient-to-br from-amber-500 to-orange-500 rounded-xl p-3">
                    <FaHandshake className="text-2xl text-white" />
                  </div>
                  <div>
                    <h4 className="text-lg font-bold text-gray-800 dark:text-gray-200 mb-2">ارتباط با صنعت</h4>
                    <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
                      شبکه‌سازی با سرمایه‌گذاران، متخصصان و صنعتگران
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <p className="text-base md:text-lg text-gray-700 dark:text-gray-300 leading-relaxed text-justify">
              جشنواره امید با هدف حمایت از نوآوری و کارآفرینی فناوری‌محور در منطقه، بستری مناسب برای 
              ایده‌پردازان و کارآفرینان جوان فراهم کرد. این جشنواره در سه مرحله برگزار شد و شرکت‌کنندگان 
              فرصت دریافت مشاوره تخصصی، آموزش‌های کاربردی و ارتباط مستقیم با سرمایه‌گذاران را داشتند.
            </p>
          </div>
        </motion.div>

        {/* Event Timeline - Enhanced Visual Timeline */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 1.0 }}
          className="max-w-5xl mx-auto mb-12 md:mb-16"
        >
          <div className="bg-gradient-to-br from-slate-100 to-slate-200 dark:from-gray-800 dark:to-slate-900 rounded-3xl p-8 md:p-10 shadow-2xl border-2 border-slate-300 dark:border-slate-700">
            <h3 className="text-2xl md:text-3xl font-bold text-center mb-10 text-gray-800 dark:text-gray-200 flex items-center justify-center gap-3">
              <FaCalendarAlt className="text-slate-600 dark:text-slate-400" />
              تایم‌لاین برگزاری جشنواره
            </h3>
            
            <div className="relative">
              {/* Vertical Line */}
              <div className="absolute right-6 md:right-1/2 top-0 bottom-0 w-1 bg-gradient-to-b from-purple-500 via-blue-500 to-emerald-500"></div>
              
              <div className="space-y-8">
                {/* Event 1 */}
                <motion.div
                  initial={{ opacity: 0, x: -50 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5 }}
                  className="relative"
                >
                  <div className="flex items-start gap-6 md:justify-end md:pr-12">
                    <div className="absolute right-3.5 md:right-[calc(50%-14px)] w-7 h-7 bg-purple-600 rounded-full border-4 border-white dark:border-gray-800 shadow-lg z-10"></div>
                    <div className="mr-12 md:mr-0 md:w-5/12 bg-gradient-to-br from-purple-100 to-purple-200 dark:from-purple-900/50 dark:to-purple-800/50 rounded-2xl p-6 shadow-xl border-2 border-purple-300 dark:border-purple-700">
                      <div className="flex items-center gap-3 mb-3">
                        <FaTrophy className="text-2xl text-purple-600 dark:text-purple-400" />
                        <h4 className="font-bold text-lg md:text-xl text-gray-800 dark:text-gray-200">افتتاحیه و کمپ امید</h4>
                      </div>
                      <p className="text-purple-700 dark:text-purple-300 text-lg font-bold mb-2">۲۱ آبان ۱۴۰۳</p>
                      <p className="text-sm text-gray-600 dark:text-gray-400">شروع جشنواره با حضور پرشور شرکت‌کنندگان</p>
                    </div>
                  </div>
                </motion.div>

                {/* Event 2 */}
                <motion.div
                  initial={{ opacity: 0, x: 50 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: 0.2 }}
                  className="relative"
                >
                  <div className="flex items-start gap-6 md:pl-12">
                    <div className="absolute right-3.5 md:right-[calc(50%-14px)] w-7 h-7 bg-blue-600 rounded-full border-4 border-white dark:border-gray-800 shadow-lg z-10"></div>
                    <div className="mr-12 md:mr-0 md:ml-auto md:w-5/12 bg-gradient-to-br from-blue-100 to-blue-200 dark:from-blue-900/50 dark:to-blue-800/50 rounded-2xl p-6 shadow-xl border-2 border-blue-300 dark:border-blue-700">
                      <div className="flex items-center gap-3 mb-3">
                        <FaRocket className="text-2xl text-blue-600 dark:text-blue-400" />
                        <h4 className="font-bold text-lg md:text-xl text-gray-800 dark:text-gray-200">کمپ باور</h4>
                      </div>
                      <p className="text-blue-700 dark:text-blue-300 text-lg font-bold mb-2">۲۸ آبان ۱۴۰۳</p>
                      <p className="text-sm text-gray-600 dark:text-gray-400">توسعه و بهبود ایده‌های کسب‌وکار</p>
                    </div>
                  </div>
                </motion.div>

                {/* Event 3 */}
                <motion.div
                  initial={{ opacity: 0, x: -50 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: 0.4 }}
                  className="relative"
                >
                  <div className="flex items-start gap-6 md:justify-end md:pr-12">
                    <div className="absolute right-3.5 md:right-[calc(50%-14px)] w-7 h-7 bg-emerald-600 rounded-full border-4 border-white dark:border-gray-800 shadow-lg z-10"></div>
                    <div className="mr-12 md:mr-0 md:w-5/12 bg-gradient-to-br from-emerald-100 to-emerald-200 dark:from-emerald-900/50 dark:to-emerald-800/50 rounded-2xl p-6 shadow-xl border-2 border-emerald-300 dark:border-emerald-700">
                      <div className="flex items-center gap-3 mb-3">
                        <FaAward className="text-2xl text-emerald-600 dark:text-emerald-400" />
                        <h4 className="font-bold text-lg md:text-xl text-gray-800 dark:text-gray-200">کمپ هنر/فن آفرین و اختتامیه</h4>
                      </div>
                      <p className="text-emerald-700 dark:text-emerald-300 text-lg font-bold mb-2">۱۲ آذر ۱۴۰۳</p>
                      <p className="text-sm text-gray-600 dark:text-gray-400">پایان موفق جشنواره با تقدیر از برگزیدگان</p>
                    </div>
                  </div>
                </motion.div>
              </div>
            </div>

            {/* Location Info */}
            <div className="mt-8 pt-6 border-t-2 border-slate-300 dark:border-slate-700">
              <div className="flex items-center justify-center gap-3 text-gray-700 dark:text-gray-300">
                <FaMapMarkerAlt className="text-xl text-slate-600 dark:text-slate-400" />
                <span className="font-semibold">مکان برگزاری:</span>
                <span>سالن همایش بین‌المللی و مرکز رشد و کارآفرینی بین‌المللی دانشگاه شمال</span>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Event Gallery */}
        <EventGallery />

        {/* Final Message and CTA */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 1.2 }}
          className="max-w-5xl mx-auto"
        >
          <div className="bg-gradient-to-br from-white via-blue-50 to-purple-50 dark:from-gray-800 dark:via-gray-700 dark:to-gray-800 rounded-3xl p-8 md:p-12 shadow-2xl border-2 border-blue-300 dark:border-blue-700">
            <div className="text-center">
              <motion.div
                animate={{ scale: [1, 1.1, 1] }}
                transition={{ duration: 2, repeat: Infinity, repeatDelay: 3 }}
                className="inline-block mb-6"
              >
                <div className="bg-gradient-to-r from-gray-600 to-gray-700 text-white px-8 py-4 rounded-2xl font-bold text-lg shadow-2xl">
                  <FaCheckCircle className="inline ml-2 text-2xl" />
                  جشنواره با موفقیت به پایان رسید
                </div>
              </motion.div>
              
              <h2 className="text-3xl md:text-4xl font-bold mb-6 bg-gradient-to-r from-gray-700 to-gray-600 dark:from-gray-300 dark:to-gray-400 bg-clip-text text-transparent">
                سپاس از همراهی شما
              </h2>
              
              <p className="text-lg md:text-xl text-gray-700 dark:text-gray-300 mb-8 leading-relaxed max-w-3xl mx-auto">
                از تمامی شرکت‌کنندگان، مربیان، داوران و حامیان جشنواره امید صمیمانه تشکر می‌کنیم. 
                این موفقیت حاصل همراهی و تلاش مشترک همه شما بود.
              </p>

              {/* Call to Action Buttons */}
              <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-8">
                <motion.button
                  onClick={() => navigate('/')}
                  whileHover={{ scale: 1.05, y: -2 }}
                  whileTap={{ scale: 0.95 }}
                  className="inline-flex items-center gap-3 px-8 py-4 bg-gradient-to-r from-primary-600 to-secondary-600 hover:from-primary-700 hover:to-secondary-700 text-white rounded-2xl font-bold text-lg shadow-xl hover:shadow-2xl transition-all"
                >
                  <FaCheckCircle className="text-xl" />
                  مشاهده جشنواره‌های فعال
                </motion.button>

                <motion.button
                  onClick={() => navigate('/festivals')}
                  whileHover={{ scale: 1.05, y: -2 }}
                  whileTap={{ scale: 0.95 }}
                  className="inline-flex items-center gap-3 px-8 py-4 bg-white dark:bg-gray-700 border-2 border-primary-600 dark:border-primary-500 text-primary-700 dark:text-primary-400 rounded-2xl font-bold text-lg shadow-lg hover:shadow-xl transition-all"
                >
                  <FaTrophy className="text-xl" />
                  همه جشنواره‌ها
                </motion.button>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
}

export default Festival;